import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Upload,
  Row,
  Col,
  Card,
  Avatar,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import { withBase } from "../../../config/env";

import dayjs from "dayjs";
import {
  fetchCountries as apiFetchCountries,
  fetchStates as apiFetchStates,
  fetchCities as apiFetchCities,
  fetchFirms as apiFetchFirms,
  updateLawyer as apiUpdateLawyer,
} from "../../../api/lawyerApi";
import { toastError, toastSuccess } from "../../../utils/toast";

export default function EditLawyerModal({ open, onClose, lawyer, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [firms, setFirms] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const data = await apiFetchCountries();
      setCountries(data);
    } catch {
      toastError("Failed to load countries");
    } finally {
      setLoadingCountries(false);
    }
  };

  const fetchFirms = async () => {
    try {
      const data = await apiFetchFirms();
      setFirms(data);
    } catch {
      toastError("Failed to load firms");
    }
  };

  const fetchStates = async (countryId) => {
    setLoadingStates(true);
    try {
      const data = await apiFetchStates(countryId);
      setStates(data);
    } catch {
      toastError("Failed to load states");
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchCities = async (stateId) => {
    setLoadingCities(true);
    try {
      const data = await apiFetchCities(stateId);
      setCities(data);
    } catch {
      toastError("Failed to load cities");
    } finally {
      setLoadingCities(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCountries();
      fetchFirms();
    }
  }, [open]);

  useEffect(() => {
    if (lawyer && open) {
      const values = {
        ...lawyer,
        date_of_birth: lawyer.date_of_birth ? dayjs(lawyer.date_of_birth) : null,
        license_issue_date: lawyer.license_issue_date ? dayjs(lawyer.license_issue_date) : null,
        license_expiry_date: lawyer.license_expiry_date ? dayjs(lawyer.license_expiry_date) : null,
      };

      form.setFieldsValue(values);

      if (lawyer.image) {
        setImagePreview(withBase(lawyer.image));
      }

      const loadLocationData = async () => {
        if (lawyer.country_id) {
          await fetchStates(lawyer.country_id);
          if (lawyer.state_id) {
            await fetchCities(lawyer.state_id);
          }
          form.setFieldsValue({
            country_id: lawyer.country_id,
            state_id: lawyer.state_id,
            city_id: lawyer.city_id,
          });
        }
      };

      loadLocationData();
    }
  }, [lawyer, open, form]);

  const handleImageChange = (file) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    return false;
  };

  const handleCountryChange = (countryId) => {
    setStates([]);
    setCities([]);
    form.setFieldsValue({ state_id: null, city_id: null });
    if (countryId) {
      fetchStates(countryId);
    }
  };

  const handleStateChange = (stateId) => {
    setCities([]);
    form.setFieldsValue({ city_id: null });
    if (stateId) {
      fetchCities(stateId);
    }
  };

  const handleUpdate = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();

      // append all fields except confirm_password; skip null/empty
      Object.entries(values).forEach(([key, val]) => {
        if (key === "confirm_password") return; // never send confirm
        if (val === null || val === "") return;

        if (dayjs.isDayjs(val)) {
          formData.append(key, val.format("YYYY-MM-DD"));
        } else {
          formData.append(key, val);
        }
      });

      // image (optional)
      if (imageFile) {
        formData.append("image", imageFile);
      }

      await apiUpdateLawyer(lawyer.user_id, formData);
      toastSuccess("Lawyer updated successfully");
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error("Update error:", error.response?.data || error.message);
      toastError(error.response?.data?.message || "Failed to update lawyer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<span style={{ fontSize: "1.25rem", fontWeight: 600 }}>Edit Lawyer</span>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      style={{ top: 20 }}
      destroyOnClose
    >
      <Form layout="vertical" form={form} onFinish={handleUpdate}>
        <Card
          title="Basic Information"
          bordered={false}
          headStyle={{ border: "none", padding: "0 0 16px 0" }}
          bodyStyle={{ padding: "16px 0" }}
        >
          <Row gutter={24}>
            <Col span={8}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Upload.Dragger
                  name="image"
                  beforeUpload={handleImageChange}
                  accept="image/*"
                  maxCount={1}
                  showUploadList={false}
                  style={{ width: "100%", marginBottom: 16 }}
                >
                  {imagePreview ? (
                    <Avatar src={imagePreview} size={120} style={{ margin: "0 auto" }} />
                  ) : (
                    <div style={{ padding: 16 }}>
                      <p className="ant-upload-drag-icon">
                        <UserOutlined style={{ fontSize: "32px" }} />
                      </p>
                      <p className="ant-upload-text">Upload Profile Photo</p>
                    </div>
                  )}
                </Upload.Dragger>
              </div>
            </Col>
            <Col span={16}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Full Name"
                    name="name"
                    rules={[{ required: true, message: "Please input full name" }]}
                  >
                    <Input placeholder="Full name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: "Please input email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input placeholder="Email address" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Role"
                    name="role"
                    rules={[{ required: true, message: "Please select role" }]}
                  >
                    <Select
                      placeholder="Select Role"
                      options={[
                        { value: "lawyer", label: "Lawyer" },
                        { value: "admin", label: "Admin" },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true, message: "Please select status" }]}
                  >
                    <Select
                      placeholder="Select Status"
                      options={[
                        { value: "active", label: "Active" },
                        { value: "inactive", label: "Inactive" },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        <Card
          title="Personal Details"
          bordered={false}
          headStyle={{ border: "none", padding: "16px 0" }}
          bodyStyle={{ padding: "16px 0" }}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="Date of Birth" name="date_of_birth">
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Gender" name="gender">
                <Select
                  placeholder="Select Gender"
                  options={[
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" },
                    { value: "other", label: "Other" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="CNIC No" name="cnic_no">
                <Input placeholder="e.g. 35202-xxxxxxx-x" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Mobile" name="mobile">
                <Input placeholder="Mobile number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item hidden label="Firm" name="firm_id" initialValue={1}>
                <Input placeholder="Firm" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label="Address" name="address">
                <Input.TextArea rows={2} placeholder="Full address" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Location Information" bordered={false}>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="Country" name="country_id">
                <Select
                  showSearch
                  onChange={handleCountryChange}
                  options={countries.map((c) => ({ value: c.id, label: c.name }))}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="Select Country"
                  loading={loadingCountries}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="State" name="state_id">
                <Select
                  showSearch
                  onChange={handleStateChange}
                  options={states.map((s) => ({ value: s.id, label: s.name }))}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="Select State"
                  loading={loadingStates}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="City" name="city_id">
                <Select
                  showSearch
                  options={cities.map((c) => ({ value: c.id, label: c.name }))}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="Select City"
                  loading={loadingCities}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label="Address" name="address">
                <Input.TextArea rows={2} placeholder="Full address" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Professional Information"
          bordered={false}
          headStyle={{ border: "none", padding: "16px 0" }}
          bodyStyle={{ padding: "16px 0 0 0" }}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="License Number" name="license_number">
                <Input placeholder="License number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Specialization" name="specialization">
                <Input placeholder="Area of specialization" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="License Issue Date" name="license_issue_date">
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="License Expiry Date" name="license_expiry_date">
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Optional Password Update */}
        <Card
          title="Security (Optional)"
          bordered={false}
          headStyle={{ border: "none", padding: "16px 0" }}
          bodyStyle={{ padding: "16px 0" }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="New Password"
                name="password"
                rules={[
                  // Optional field: only validate length if user typed something
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || value.length >= 6) return Promise.resolve();
                      return Promise.reject(new Error("Password must be at least 6 characters"));
                    },
                  }),
                ]}
                hasFeedback
              >
                <Input.Password placeholder="Leave blank to keep existing password" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Confirm New Password"
                name="confirm_password"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const pwd = getFieldValue("password");
                      // If no new password is provided, confirm is optional and should pass
                      if (!pwd && !value) return Promise.resolve();
                      if (pwd && value === pwd) return Promise.resolve();
                      return Promise.reject(new Error("Passwords do not match"));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Re-enter new password" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
          <Button style={{ marginRight: 16, minWidth: 100 }} onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading} style={{ minWidth: 120 }}>
            Update Lawyer
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
